import React, { useEffect, useRef, useState } from "react"
import { Dropdown, Badge, Button, Nav, Modal, Offcanvas } from "react-bootstrap"
import { useSelector } from "react-redux"
import { useNotifications } from "../../../hooks/defaults/useNotifications"
import { FaBell, FaArrowLeft } from "react-icons/fa"
import { FiX } from "react-icons/fi"
import NotificationItem from "./NotificationItem"
import "./NotificationBell.css"

const NotificationBell = ({ socket, className = "", shouldClose = false }) => {
	const [open, setOpen] = useState(false)
	const [activeTab, setActiveTab] = useState("unread")
	const [animate, setAnimate] = useState(false)
	const [isMobileView, setIsMobileView] = useState(false)
	const userId = useSelector((state) => state.global?.userId)
	const {
		unreadNotifications,
		archivedNotifications,
		unreadCount,
		handleMarkAsRead,
		handleDismiss,
		handleMarkAllAsRead,
		loadArchivedNotifications,
		loadInitialNotifications,
	} = useNotifications(socket)
	const prevUnreadCount = useRef(unreadCount)
	const lastLoadedUserId = useRef(null)

	// Carregar notificações ao montar/refresh quando usuário estiver logado (GET). Só [userId] nas deps para evitar loop (loadInitialNotifications mudava a cada render e re-disparava o efeito).
	useEffect(() => {
		if (!userId) return
		if (lastLoadedUserId.current === userId) return
		lastLoadedUserId.current = userId
		loadInitialNotifications(userId)
	}, [userId])

	// Detectar se é mobile baseado no tamanho da tela
	useEffect(() => {
		const checkMobile = () => {
			setIsMobileView(window.innerWidth <= 768)
		}
		
		checkMobile()
		window.addEventListener('resize', checkMobile)
		return () => window.removeEventListener('resize', checkMobile)
	}, [])

	useEffect(() => {
		if (activeTab !== "archived") {
			setActiveTab("unread")
		}
	}, [archivedNotifications, activeTab])

	useEffect(() => {
		if (unreadCount > prevUnreadCount.current) {
			setAnimate(true)

			const timeout1 = setTimeout(() => {
				setAnimate(false)
			}, 3000)

			return () => {
				clearTimeout(timeout1)
			}
		}

		prevUnreadCount.current = unreadCount
	}, [unreadCount])

	// Fechar menu mobile ao pressionar ESC e gerenciar classe no body
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === 'Escape' && open && isMobileView) {
				setOpen(false)
			}
		}

		if (open && isMobileView) {
			document.addEventListener('keydown', handleKeyDown)
			document.body.style.overflow = 'hidden' // Prevenir scroll do body
			// adicionar classe para indicar notificações abertas (ajuda no CSS de sobreposição)
			document.body.classList.add('notifications-open')
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown)
			document.body.style.overflow = ''
			document.body.classList.remove('notifications-open')
		}
	}, [open, isMobileView])

	// Fechar quando shouldClose for true (quando o menu mobile estiver aberto)
	useEffect(() => {
		if (shouldClose && open) {
			setOpen(false)
		}
	}, [shouldClose, open])

	const handleSetActiveTab = async (key) => {
		setActiveTab(key)
		if (key === "archived") {
			await loadArchivedNotifications()
		}
	}

	const handleToggle = (nextShow) => {
		setOpen(nextShow)
		if (nextShow) {
			setAnimate(false)
		}
	}

	const handleClose = () => {
		setOpen(false)
	}

	const dropdownMenuStyle = {
		width: isMobileView ? "95vw" : "380px",
		maxHeight: isMobileView ? "70vh" : "400px",
		overflowY: "auto",
	}

	// Renderizar conteúdo das notificações
	const renderNotificationContent = () => {
		return (
			<>
				{/* Navegação por abas - Otimizada para mobile */}
				<div className="notification-tabs-container">
					<Nav
						variant="pills"
						activeKey={activeTab}
						onSelect={(key) => handleSetActiveTab(key)}
						className="notification-nav-pills"
						fill
					>
						<Nav.Item>
							<Nav.Link eventKey="unread" className="notification-nav-pill">
								<span>Novas</span>
								{unreadCount > 0 && (
									<Badge bg="danger" className="ms-2">
										{unreadCount}
									</Badge>
								)}
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link eventKey="archived" className="notification-nav-pill">
								Lidas
							</Nav.Link>
						</Nav.Item>
					</Nav>
				</div>

				{/* Conteúdo das notificações */}
				<div className="notification-content-area">
					{activeTab === "unread" && (
						<>
							<div className="notification-header">
								<div className="notification-header-content">
									<h6 className="notification-title">Notificações</h6>
									{unreadNotifications.length > 0 && (
										<Button
											variant="link"
											size="sm"
											onClick={handleMarkAllAsRead}
											className="mark-all-button"
										>
											Marcar todas como lidas
										</Button>
									)}
								</div>
							</div>

							<div className="notification-list">
								{unreadNotifications.length === 0 ? (
									<div className="notification-empty">
										<div className="empty-state">
											<FaBell className="empty-icon" />
											<p className="empty-text">Nenhuma notificação nova</p>
											<small className="empty-subtext">
												Suas notificações aparecerão aqui
											</small>
										</div>
									</div>
								) : (
								unreadNotifications.map((notification) => (
									<NotificationItem
										key={notification.deliveryId ?? notification._id ?? notification.id}
										notification={notification}
										onRead={handleMarkAsRead}
										onDismiss={handleDismiss}
									/>
								))
								)}
							</div>
						</>
					)}

					{activeTab === "archived" && (
						<>
							<div className="notification-header">
								<h6 className="notification-title">Notificações arquivadas</h6>
							</div>

							<div className="notification-list">
								{Object.values(archivedNotifications).every(
									(arr) => arr.length === 0
								) ? (
									<div className="notification-empty">
										<div className="empty-state">
											<FaBell className="empty-icon" />
											<p className="empty-text">Nenhuma notificação arquivada</p>
											<small className="empty-subtext">
												Notificações lidas aparecerão aqui
											</small>
										</div>
									</div>
								) : (
								archivedNotifications["read"].map((notification) => (
									<NotificationItem
										key={notification.deliveryId ?? notification._id ?? notification.id}
										notification={notification}
										onRead={handleMarkAsRead}
										onDismiss={handleDismiss}
									/>
								))
								)}
							</div>
						</>
					)}
				</div>
			</>
		)
	}

	// Versão mobile - Offcanvas (menu lateral)
	if (isMobileView) {
		return (
			<>                <button
					type="button"
					className={`notification-bell-mobile ${className}`}
					onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(prev => !prev); }}
					aria-label="Abrir notificações"
				>
					<div className="notification-icon-container">
						<FaBell className={`notification-icon ${animate ? 'shake-animation' : ''}`} />
						{unreadCount > 0 && !animate && (
							<Badge bg="danger" className="notification-badge-mobile">
								{unreadCount > 99 ? '99+' : unreadCount}
							</Badge>
						)}
						{animate && (
							<span className="new-notification-indicator">
								<span className="red-dot" />
							</span>
						)}
					</div>
				</button>			<Offcanvas
				show={open}
				onHide={handleClose}
				placement="end"
				className="notification-offcanvas"
				backdrop={true}
				scroll={false}
				container={typeof document !== 'undefined' ? document.body : undefined}
			>
					<Offcanvas.Header className="notification-offcanvas-header">
						<Offcanvas.Title className="notification-offcanvas-title">
							<FaBell className="me-2" />
							Notificações
						</Offcanvas.Title>
						<button
							className="notification-close-button"
							onClick={handleClose}
							aria-label="Fechar notificações"
						>
							<FiX size={24} />
						</button>
					</Offcanvas.Header>
					<Offcanvas.Body className="notification-offcanvas-body">
						{renderNotificationContent()}
					</Offcanvas.Body>
				</Offcanvas>
			</>
		)
	}

	// Versão desktop - Dropdown
	return (
		<Dropdown
			className={`notification-bell ${className}`}
			autoClose="outside"
			show={open}
			onToggle={handleToggle}
		>
			<Dropdown.Toggle
				variant="link"
				id="notification-dropdown"
				className="nav-link p-0 d-flex align-items-center notification-toggle"
			>
				<div className="notification-icon-container">
					<FaBell className={`notification-icon ${animate ? 'shake-animation' : ''}`} />
					{unreadCount > 0 && !animate && (
						<Badge bg="danger" className="notification-badge">
							{unreadCount > 99 ? '99+' : unreadCount}
						</Badge>
					)}
					{animate && (
						<span className="new-notification-indicator">
							<span className="red-dot" />
						</span>
					)}
				</div>
			</Dropdown.Toggle>

			<Dropdown.Menu
				className="notification-menu-desktop"
				style={dropdownMenuStyle}
				align="end"
			>
				{renderNotificationContent()}
			</Dropdown.Menu>
		</Dropdown>
	)
}

export default NotificationBell
